import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    Animated
} from "react-native";
import type {
    ChartSeriesFadeConstantType
} from "./type";
import {
    useMotionTiming
} from "../../helpers/motion";

const useChartSeriesFade = ({
    customTheme,
    seriesKeys,
    hiddenKeys
}: ChartSeriesFadeConstantType): Record<string, number> => {
    const hideTiming = useMotionTiming("listRemove", customTheme, "fade");

    const showTiming = useMotionTiming("listInsert", customTheme, "fade");

    const progress = useRef(new Animated.Value(1)).current;

    const originRef = useRef<Record<string, number>>({});

    const targetRef = useRef<Record<string, number>>({});

    const [
        progressValue,
        setProgressValue
    ] = useState<number>(1);

    const getTarget = (key: string): number => hiddenKeys.includes(key) ? 0 : 1;

    const levels = Object.fromEntries(seriesKeys.map((key) => {
        const target = targetRef.current[key] ?? getTarget(key);

        const origin = originRef.current[key] ?? target;

        return [
            key,
            origin + (target - origin) * progressValue
        ];
    }));

    useEffect(() => {
        const listenerID = progress.addListener(({
            value
        }) => setProgressValue(value));

        return () => {
            progress.removeListener(listenerID);
        };
    }, [
        progress
    ]);

    useEffect(() => {
        const nextTarget = Object.fromEntries(seriesKeys.map((key) => [
            key,
            getTarget(key)
        ]));

        const isChanged = seriesKeys.some((key) => targetRef.current[key] !== undefined && targetRef.current[key] !== nextTarget[key]);

        originRef.current = isChanged ? levels : nextTarget;

        targetRef.current = nextTarget;

        if(!isChanged) {
            return;
        }

        progress.setValue(0);

        const isHiding = seriesKeys.some((key) => (levels[key] ?? 1) > (nextTarget[key] ?? 1));

        const timing = isHiding ? hideTiming : showTiming;

        const animation = Animated.timing(progress, {
            duration: timing.duration,
            useNativeDriver: false,
            easing: timing.easing,
            toValue: 1
        });

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        seriesKeys.join(","),
        hiddenKeys.join(",")
    ]);

    return levels;
};
export default useChartSeriesFade;
